import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { db, auth } from "../firebase/firebaseConfig";
import { motion, AnimatePresence } from "framer-motion";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
} from "firebase/firestore";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

function Dashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date()); 
  const [showPast, setShowPast] = useState(false);
  const [tab, setTab] = useState("attended");
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateStreak = () => {
      const today = new Date().toDateString();
      const lastVisit = localStorage.getItem("lastVisitDate");
      const savedStreak = parseInt(localStorage.getItem("userStreak") || "0");
      if (lastVisit === today) {
        setStreak(savedStreak);
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (lastVisit === yesterday.toDateString()) {
          const newStreak = savedStreak + 1;
          setStreak(newStreak);
          localStorage.setItem("userStreak", newStreak);
        } else {
          setStreak(1);
          localStorage.setItem("userStreak", 1);
        }
        localStorage.setItem("lastVisitDate", today);
      }
    };

    const fetchBookings = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      try {
        const q = query(collection(db, "bookings"), where("user", "==", currentUser.email));
        const snap = await getDocs(q);
        setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error fetching bookings:", error);
      }
    };

    updateStreak();
    fetchBookings();
  }, []);

  const getSessionTimes = (date, time) => {
    const start = new Date(`${date} ${time}`);
    const end = new Date(start.getTime() + 45 * 60000); 
    return { start, end };
  };

  const upcomingActive = bookings.filter((b) => {
    const { end } = getSessionTimes(b.date, b.time);
    return currentTime <= end && b.status !== "Completed" && b.status !== "Cancelled";
  });

  const attended = bookings.filter((b) => b.status === "Completed");

  const notAttended = bookings.filter((b) => {
    const { end } = getSessionTimes(b.date, b.time);
    return currentTime > end && b.status !== "Completed" && b.status !== "Cancelled";
  });

  const cancelled = bookings.filter((b) => b.status === "Cancelled");

  const cancelGD = async (id) => {
    if (!window.confirm("Abort mission? 🛑")) return;
    await updateDoc(doc(db, "bookings", id), { status: "Cancelled" });
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "Cancelled" } : b)));
  };

  const renderSessionStatus = (g) => {
    const { start, end } = getSessionTimes(g.date, g.time);
    const diff = start - currentTime;
    const isLive = currentTime >= start && currentTime <= end;

    if (isLive) {
      return (
        <motion.button 
          whileHover={{ scale: 1.05 }} 
          style={joinBtn}
          onClick={() => navigate("/gdroom", { state: { bookingId: g.id, topic: g.topic } })}
        >
          Go to Group Discussion 🔓
        </motion.button>
      );
    }

    if (diff > 0 && diff <= 300000) {
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      return (
        <div style={waiting}>
          ⚠️ Starts in {mins}:{secs < 10 ? `0${secs}` : secs}
        </div>
      );
    }

    return <div style={waiting}>⏳ Scheduled</div>;
  };

  return (
    <div style={layout}>
      <Sidebar />
      <div style={content}>
        <div style={topbarContainer}>
          <h2 style={topbarTitle}>AI Based Group Discussion Evaluation</h2>
          <Topbar />
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="visible" style={main}>
          <motion.div variants={itemVariants} whileHover={{ scale: 1.01 }} style={hero}>
            <div style={streakBadge}>
               <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }}>🔥</motion.span>
               <span>{streak} Day Streak</span>
            </div>
            <h1 style={heroH1}>Practice. Analyze. Improve.</h1>
            <p style={heroP}>AI-powered Hub for Career Excellence</p>
            <motion.button whileHover={{ scale: 1.05, boxShadow: "0px 0px 20px #22c55e" }} whileTap={{ scale: 0.95 }} style={ctaBtn} onClick={() => navigate("/bookslot")}>
              🚀 Deploy New Slot
            </motion.button>
          </motion.div>

          <div style={flowContainer}>
            {[{ icon: "👤", text: "Register" }, { icon: "🤖", text: "AI Topic" }, { icon: "🎤", text: "Discussion" }, { icon: "📊", text: "Report" }, { icon: "🚀", text: "Improve" }].map((step, i) => (
              <motion.div key={i} variants={itemVariants} whileHover={interactiveHover} style={flowCard}>
                <div style={flowIcon}>{step.icon}</div>
                <p style={cardText}>{step.text}</p>
              </motion.div>
            ))}
          </div>

          <div style={grid}>
            <motion.div variants={itemVariants} whileHover={interactiveHover} style={card}>🎯 Top Topic: <b>Remote Work</b></motion.div>
            <motion.div variants={itemVariants} whileHover={interactiveHover} style={card}>📊 Trending: <b>AI Ethics</b></motion.div>
            <motion.div variants={itemVariants} whileHover={interactiveHover} style={card}>⚡ Rank: <b>Silver II</b></motion.div>
          </div>

          <div style={{ height: "40px" }}></div>

          <motion.div variants={itemVariants} whileHover={interactiveHover} style={feedbackCard}>
            <h3 style={cardHeader}>📄 AI PERFORMANCE FEEDBACK</h3>
            <p style={cardSub}>Confidence Score: <span style={{color: '#22c55e'}}>78%</span></p>
            <p style={cardSub}>Strategic Tip: <span style={{color: '#6366f1'}}>Improve clarity in transitions</span></p>
          </motion.div>

          <h2 style={sectionHeader}>Active Sessions ⚔️</h2>
          <div style={grid}>
            <AnimatePresence>
              {upcomingActive.length === 0 ? (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} style={{gridColumn: '1/-1', textAlign: 'center', padding: '20px'}}>
                  No missions currently active.
                </motion.p>
              ) : (
                upcomingActive.map((g) => (
                  <motion.div layout variants={itemVariants} whileHover={interactiveHover} key={g.id} style={cardHover}>
                    <motion.span whileHover={{ scale: 1.2, color: '#ef4444' }} style={cancel} onClick={() => cancelGD(g.id)}>❌</motion.span>
                    <h3 style={{color: '#6366f1', marginBottom: '10px'}}>{g.topic}</h3>
                    <p style={cardSub}>📅 {g.date}</p>
                    <p style={cardSub}>⏰ {g.time}</p>
                    {renderSessionStatus(g)}
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          <motion.button variants={itemVariants} whileHover={{ letterSpacing: '2px', backgroundColor: 'rgba(99, 102, 241, 0.1)' }} style={pastBtn} onClick={() => setShowPast(true)}>
            📜 ACCESS ARCHIVES
          </motion.button>

          <AnimatePresence>
            {showPast && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={overlay} onClick={() => setShowPast(false)}>
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} style={modal} onClick={(e) => e.stopPropagation()}>
                  <h2 style={{color: '#6366f1', marginBottom: '20px'}}>Past Missions</h2>
                  <div style={tabs}>
                    {['attended', 'not', 'cancelled'].map((t) => (
                      <button key={t} style={tab === t ? activeTab : inactiveTab} onClick={() => setTab(t)}>{t.toUpperCase()}</button>
                    ))}
                  </div>
                  <div style={modalList}>
                    {tab === "attended" && attended.map((g) => <div key={g.id} style={listItem}>✅ {g.topic}</div>)}
                    {tab === "not" && notAttended.map((g) => <div key={g.id} style={listItem}>⭕ {g.topic}</div>)}
                    {tab === "cancelled" && cancelled.map((g) => <div key={g.id} style={listItem}>🚫 {g.topic}</div>)}
                  </div>
                  <button onClick={() => setShowPast(false)} style={closeBtn}>CLOSE ACCESS</button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

const interactiveHover = { scale: 1.02, borderColor: "#6366f1", boxShadow: "0px 0px 25px rgba(99, 102, 241, 0.3)", background: "rgba(30, 41, 59, 0.6)" };
const layout = { display: "flex", background: "#050505", backgroundImage: "radial-gradient(circle at 50% -20%, #1e1b4b, #050505)", color: "white", minHeight: "100vh", fontFamily: "'Inter', sans-serif" };
const content = { flex: 1, marginLeft: "250px" };
const topbarContainer = { background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(10px)", padding: "15px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.05)", position: 'sticky', top: 0, zIndex: 100 };
const topbarTitle = { color: "white", margin: 0, fontSize: "18px", fontWeight: "900", letterSpacing: '2px' };
const main = { padding: "40px" };
const hero = { position: 'relative', padding: "60px", textAlign: "center", background: "linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(236, 72, 153, 0.8))", borderRadius: "30px", border: "1px solid rgba(255, 255, 255, 0.1)", boxShadow: "0 20px 40px rgba(0,0,0,0.4)", marginBottom: '40px' };
const heroH1 = { fontSize: '32px', margin: 0, fontWeight: '900', letterSpacing: '-1px' };
const heroP = { fontSize: '16px', opacity: 0.8, marginTop: '10px' };
const streakBadge = { position: 'absolute', top: '20px', right: '30px', background: 'rgba(0,0,0,0.4)', padding: '8px 20px', borderRadius: '50px', fontSize: '14px', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '8px' };
const ctaBtn = { marginTop: "25px", padding: "14px 32px", background: "#000", color: "#fff", border: "none", borderRadius: "12px", cursor: "pointer", fontWeight: "bold", fontSize: '16px' };
const flowContainer = { display: "flex", justifyContent: "space-between", marginBottom: "40px", gap: '15px' };
const flowCard = { background: "rgba(30, 41, 59, 0.4)", padding: "25px", borderRadius: "20px", textAlign: "center", border: "1px solid rgba(255, 255, 255, 0.05)", transition: "0.3s", flex: 1 };
const cardText = { fontWeight: 'bold', fontSize: '14px', marginTop: '10px' };
const flowIcon = { fontSize: "32px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: "20px", marginTop: "20px" };
const card = { background: "rgba(30, 41, 59, 0.4)", padding: "20px", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.05)", transition: "0.3s", fontSize: '15px' };
const feedbackCard = { ...card, background: "rgba(99, 102, 241, 0.1)", borderLeft: "5px solid #6366f1" };
const cardHeader = { fontSize: '14px', letterSpacing: '1px', marginBottom: '15px', color: '#6366f1' };
const cardSub = { fontSize: '14px', opacity: 0.7, margin: '5px 0' };
const sectionHeader = { marginTop: '50px', fontSize: '22px', fontWeight: '900' };
const cardHover = { ...card, position: "relative", padding: '25px' };
const cancel = { position: "absolute", right: "15px", top: "15px", cursor: "pointer" };
const joinBtn = { marginTop: "15px", background: "#22c55e", color: "white", border: "none", width: '100%', padding: "12px", borderRadius: "10px", cursor: "pointer", fontWeight: "bold" };
const waiting = { color: "#facc15", fontWeight: "bold", fontSize: '13px', marginTop: '15px', textAlign: 'center', background: 'rgba(250, 204, 21, 0.1)', padding: '8px', borderRadius: '8px' };
const pastBtn = { marginTop: "50px", background: "transparent", color: "#6366f1", padding: "15px 30px", border: "1px solid #6366f1", borderRadius: "12px", cursor: "pointer", fontWeight: "bold", fontSize: '12px', display: 'block', margin: '50px auto' };
const overlay = { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.9)", backdropFilter: 'blur(10px)', display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 };
const modal = { background: "#0a0a0a", padding: "40px", borderRadius: "30px", width: "600px", border: "1px solid #1e293b" };
const tabs = { display: "flex", gap: "10px", marginBottom: "25px", background: '#111', padding: '5px', borderRadius: '15px' };
const activeTab = { flex: 1, padding: '10px', borderRadius: '12px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: 'bold', cursor: 'pointer' };
const inactiveTab = { flex: 1, padding: '10px', borderRadius: '12px', border: 'none', background: 'transparent', color: '#444', fontWeight: 'bold', cursor: 'pointer' };
const modalList = { maxHeight: "300px", overflowY: "auto", marginBottom: "25px" };
const listItem = { padding: "15px", borderBottom: "1px solid #111", fontSize: '14px' };
const closeBtn = { width: '100%', padding: "15px", borderRadius: "12px", border: "none", cursor: "pointer", background: "#1e293b", color: "white", fontWeight: 'bold' };

export default Dashboard;